import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Loader2, Search, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Dashboard components
import {
  DashboardHeader,
  LanguageCard,
  CreateLanguageDialog,
  EditLanguageDialog,
  DeleteLanguageDialog,
  CoverImageDialog,
  ShareDialog,
  EmptyLanguagesState,
  EmptySharedState,
  NoSearchResultsState,
} from '../components/dashboard';

import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';

import { API_BASE as API } from '../config/api';

const Dashboard = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Data state
  const [activeTab, setActiveTab] = useState('my-languages');
  const [conlangs, setConlangs] = useState([]);
  const [sharedConlangs, setSharedConlangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [selectedConlang, setSelectedConlang] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareConlang, setShareConlang] = useState(null);

  // Auth redirect
  useEffect(() => {
    if (location.state?.user) return;
    if (!authLoading && !user) navigate('/login');
  }, [authLoading, user, navigate, location.state]);

  // Fetch data
  useEffect(() => {
    if (user) {
      fetchConlangs();
      fetchSharedConlangs();
    }
  }, [user]);

  const fetchConlangs = async () => {
    try {
      const response = await axios.get(`${API}/conlangs`, { withCredentials: true });
      setConlangs(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load languages');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedConlangs = async () => {
    try {
      const response = await axios.get(`${API}/conlangs/shared-with-me`, { withCredentials: true });
      setSharedConlangs(response.data);
    } catch (error) {
      console.error('Failed to load shared languages');
    }
  };

  // CRUD operations
  const handleCreate = async (data) => {
    setFormLoading(true);
    try {
      const response = await axios.post(`${API}/conlangs`, data, { withCredentials: true });
      setConlangs([...conlangs, response.data]);
      setCreateDialogOpen(false);
      toast.success('Language created!');
    } catch (error) {
      toast.error('Failed to create language');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (data) => {
    if (!selectedConlang) return;
    setFormLoading(true);
    try {
      const response = await axios.put(
        `${API}/conlangs/${selectedConlang.conlang_id}`, 
        data, 
        { withCredentials: true }
      );
      setConlangs(conlangs.map(c => 
        c.conlang_id === selectedConlang.conlang_id ? response.data : c
      ));
      setEditDialogOpen(false);
      setSelectedConlang(null);
      toast.success('Language updated!');
    } catch (error) {
      toast.error('Failed to update language');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedConlang) return;
    setFormLoading(true);
    try {
      await axios.delete(`${API}/conlangs/${selectedConlang.conlang_id}`, { withCredentials: true });
      setConlangs(conlangs.filter(c => c.conlang_id !== selectedConlang.conlang_id));
      setDeleteDialogOpen(false);
      setSelectedConlang(null);
      toast.success('Language deleted');
    } catch (error) {
      toast.error('Failed to delete language');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCoverChange = async (coverImage) => {
    if (!selectedConlang) return;
    setFormLoading(true);
    try {
      await axios.put(
        `${API}/conlangs/${selectedConlang.conlang_id}/cover-image`, 
        { cover_image: coverImage }, 
        { withCredentials: true }
      );
      setConlangs(conlangs.map(c => 
        c.conlang_id === selectedConlang.conlang_id ? {...c, cover_image: coverImage} : c
      ));
      setCoverDialogOpen(false);
      setSelectedConlang(null);
      toast.success('Cover image updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cover');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleVisibility = async (conlang) => {
    const newVisibility = conlang.visibility === 'public' ? 'private' : 'public';
    try {
      await axios.put(
        `${API}/conlangs/${conlang.conlang_id}/visibility`, 
        { visibility: newVisibility }, 
        { withCredentials: true }
      );
      setConlangs(conlangs.map(c => 
        c.conlang_id === conlang.conlang_id ? {...c, visibility: newVisibility} : c
      ));
      toast.success(`Language is now ${newVisibility}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change visibility');
    }
  };

  // Dialog openers
  const openEditDialog = (conlang) => {
    setSelectedConlang(conlang);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (conlang) => {
    setSelectedConlang(conlang);
    setDeleteDialogOpen(true);
  };

  const openCoverDialog = (conlang) => {
    setSelectedConlang(conlang);
    setCoverDialogOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const response = await axios.post(`${API}/conlangs/import-full`, data, { withCredentials: true });
      setConlangs([response.data, ...conlangs]);
      setImportDialogOpen(false);
      toast.success(`"${response.data.name}" imported successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import. Check the JSON format.');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (conlang) => {
    try {
      const response = await axios.get(`${API}/conlangs/${conlang.conlang_id}/export-full`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(response.data.content, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename || `${conlang.name}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`"${conlang.name}" exported`);
    } catch {
      toast.error('Export failed');
    }
  };

  const handleClone = async (conlangId) => {
    setFormLoading(true);
    try {
      const response = await axios.post(`${API}/conlangs/${conlangId}/clone`, {}, { withCredentials: true });
      setConlangs([response.data, ...conlangs]);
      setCreateDialogOpen(false);
      toast.success(`"${response.data.name}" cloned successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clone failed');
    } finally { setFormLoading(false); }
  };

  const handleOpenShare = (conlang) => {
    setShareConlang(conlang);
    setShareDialogOpen(true);
  };

  const handleLeave = async (conlang) => {
    if (!window.confirm(`Leave "${conlang.name}"? You will lose access.`)) return;
    try {
      await axios.post(`${API}/conlangs/${conlang.conlang_id}/leave`, {}, { withCredentials: true });
      setSharedConlangs(sharedConlangs.filter(c => c.conlang_id !== conlang.conlang_id));
      toast.success('Left shared language');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to leave'); }
  };

  // Filtered languages for current tab
  const currentLanguages = activeTab === 'my-languages' ? conlangs : sharedConlangs;
  const filteredConlangs = currentLanguages.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.native_name && c.native_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  // Render content based on state
  const renderContent = () => {
    // Has results
    if (filteredConlangs.length > 0) {
      return (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="conlangs-grid">
          {filteredConlangs.map((conlang) => (
            <LanguageCard
              key={conlang.conlang_id}
              conlang={conlang}
              isSharedTab={activeTab === 'shared-with-me'}
              onEdit={openEditDialog}
              onDelete={openDeleteDialog}
              onChangeCover={openCoverDialog}
              onToggleVisibility={handleToggleVisibility}
              onExport={handleExport}
              onShare={handleOpenShare}
              onLeave={handleLeave}
            />
          ))}
        </div>
      );
    }

    // Empty states
    if (activeTab === 'my-languages' && conlangs.length === 0) {
      return <EmptyLanguagesState onCreateClick={() => setCreateDialogOpen(true)} />;
    }
    
    if (activeTab === 'shared-with-me' && sharedConlangs.length === 0) {
      return <EmptySharedState />;
    }

    // No search results
    return <NoSearchResultsState />;
  };

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <DashboardHeader user={user} onLogout={handleLogout} />

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground" data-testid="dashboard-title">
                {activeTab === 'my-languages' ? 'My Languages' : 'Shared with Me'}
              </h1>
              <p className="text-foreground-muted mt-1">
                {activeTab === 'my-languages' 
                  ? `${conlangs.length} ${conlangs.length === 1 ? 'language' : 'languages'} in your collection`
                  : `${sharedConlangs.length} ${sharedConlangs.length === 1 ? 'language' : 'languages'} shared with you`
                }
              </p>
            </div>
            
            {activeTab === 'my-languages' && (
              <div className="flex gap-2">
                <Button 
                  onClick={() => setImportDialogOpen(true)} 
                  variant="outline"
                  className="border-border" 
                  data-testid="import-conlang-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
                <Button 
                  onClick={() => setCreateDialogOpen(true)} 
                  className="bg-primary hover:bg-primary/90" 
                  data-testid="create-conlang-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Language
                </Button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-background-paper border border-border">
              <TabsTrigger value="my-languages" data-testid="tab-my-languages">
                My Languages ({conlangs.length})
              </TabsTrigger>
              <TabsTrigger value="shared-with-me" data-testid="tab-shared-with-me">
                Shared with Me ({sharedConlangs.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Search */}
          {currentLanguages.length > 0 && (
            <div className="relative mb-6 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
              <Input
                placeholder="Search languages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-background-paper border-border"
                data-testid="search-conlangs-input"
              />
            </div>
          )}

          {/* Content */}
          {renderContent()}
        </div>
      </main>

      {/* Dialogs */}
      <CreateLanguageDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreate}
        onClone={handleClone}
        loading={formLoading}
      />

      <EditLanguageDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        conlang={selectedConlang}
        onSubmit={handleEdit}
        loading={formLoading}
      />

      <DeleteLanguageDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        conlang={selectedConlang}
        onConfirm={handleDelete}
        loading={formLoading}
      />

      <CoverImageDialog
        open={coverDialogOpen}
        onOpenChange={setCoverDialogOpen}
        conlang={selectedConlang}
        onSelectCover={handleCoverChange}
        loading={formLoading}
      />

      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        conlang={shareConlang}
      />

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-md" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Import Language</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Upload a JSON file from a previous full export to import a complete language.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors" data-testid="import-drop-zone">
              <Upload className="w-8 h-8 text-foreground-muted mb-2" />
              <span className="text-foreground-muted text-sm">{importing ? 'Importing...' : 'Click to select JSON file'}</span>
              <input type="file" accept=".json" className="hidden" onChange={handleImportFile} disabled={importing} data-testid="import-file-input" />
            </label>
            {importing && (
              <div className="flex items-center justify-center gap-2 text-primary">
                <Loader2 className="w-4 h-4 animate-spin" /> Importing...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
